import React from 'react';
import PropTypes from 'prop-types';

const SettingsModal = ({ isOpen, onClose, cciAdjusted, onToggleCciAdjustment }) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="settings-modal-overlay fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="settings-modal bg-white rounded-lg shadow-xl p-6 w-full max-w-5xl mx-4 flex flex-col max-h-[85vh]">
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <h3 className="text-xl font-semibold">Settings</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close settings modal">
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        <div className="settings-content overflow-y-auto">
          <div className="data-definitions-section mb-6 p-4 border border-gray-300 rounded-lg bg-white">
            <h4 className="text-xl font-semibold mb-3 text-gray-800 border-b pb-2">Data Source &amp; Definitions</h4>

            <div className="data-source mb-4">
              <h5 className="text-md font-semibold mb-1 text-gray-700">Data Source</h5>
              <p className="text-sm text-gray-600">
                Analysis conducted on 11.9M asthma patients found in IQVIA claims data with an asthma treatment or diagnosis from
                2019-2023 and their full history.
              </p>
            </div>

            <div className="unmet-needs-definitions">
              <h5 className="text-md font-semibold mb-2 text-gray-700">Unmet Need Definitions</h5>
              <div className="space-y-3 text-sm text-gray-600">
                <div>
                  <p className="font-medium text-gray-700">Excessive Steroid Use on Double Therapy:</p>
                  <p>
                    Patients on double therapy who receive more than 450mg of OCS in a year. OCS is converted to prednisone
                    equivalent milligrams, calculated using prescribed dose, quantity, and days supply.
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-700">Failure to Escalate after more than 3 ED/ER visits:</p>
                  <p>
                    Patients on double therapy who experience exacerbations but are not escalated to triple therapy or biologics.
                    Exacerbations are defined as visits to the ED/ER with a primary asthma diagnosis code or specific asthma
                    treatments (e.g., emergency airway insertion, airway inhalation treatment, or nebulizer with compression).
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-700">Delay from Double to Advanced Therapy:</p>
                  <p>
                    Patients who are escalated from double to advanced therapy more than 60 days after their first exacerbation.
                    Exacerbations are defined as visits to the ED/ER with a primary asthma diagnosis code or specific asthma
                    treatments (e.g., emergency airway insertion, airway inhalation treatment, or nebulizer with compression).
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-700">Open triple therapy:</p>
                  <p>Patients on double therapy who are escalated to a separate LAMA therapy.</p>
                </div>
                <div>
                  <p className="font-medium text-gray-700">Biologic before triple:</p>
                  <p>Patients on double therapy who receive a biologic prior to receiving a triple therapy.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="cci-adjustment-setting mb-4 p-4 bg-blue-50 rounded-lg">
            <div className="flex flex-col gap-3">
              <div className="cci-description">
                <h4 className="text-lg font-semibold mb-2 text-blue-800">Compare Health Systems with Fair Adjustment</h4>
                <p className="text-gray-700">
                  The unmet need percentages can be adjusted for patient severity based on the Charlson Comorbidity Index (CCI).
                  This helps create a fair comparison when patient severity and complexity is related to patient care.
                </p>
              </div>
              <button
                onClick={onToggleCciAdjustment}
                className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
                  cciAdjusted ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
                }`}
              >
                {cciAdjusted ? 'Disable CCI Adjustment' : 'Adjust comparison across health systems for CCI'}
              </button>
              {cciAdjusted && (
                <div className="mt-2 text-sm text-blue-600 bg-blue-50 p-2 border-l-4 border-blue-600">
                  <p>
                    <strong>CCI adjustment is enabled:</strong> Unmet need percentages are now normalized based on patient
                    complexity.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="cci-category-explanation p-4 border border-gray-200 rounded-lg">
            <h4 className="text-lg font-semibold mb-2 text-gray-700">Charlson Index Categories</h4>
            <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
              <li>
                <strong>0-0.5:</strong> Very Low
              </li>
              <li>
                <strong>0.5-1.0:</strong> Low
              </li>
              <li>
                <strong>1.0-1.5:</strong> Low/Medium
              </li>
              <li>
                <strong>1.5-2.0:</strong> Medium
              </li>
              <li>
                <strong>2.0-2.5:</strong> Medium/High
              </li>
              <li>
                <strong>2.5-3.0:</strong> High
              </li>
              <li>
                <strong>&gt;3.0:</strong> Very High
              </li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end mt-4 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

SettingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cciAdjusted: PropTypes.bool.isRequired,
  onToggleCciAdjustment: PropTypes.func.isRequired,
};

export default SettingsModal;
